// Import analytics SDKs
import mixpanel from "mixpanel-browser";
import posthog from "posthog-js";

// Mobile detection
import UA from "ua-parser-js";
const ua = new UA();
const isMobile = ua.getResult().device["type"] == "mobile";

// Track if analytics services are enabled
let mixpanelEnabled = false;
let posthogEnabled = false;

// Store tour properties for use in events
let tourProperties = {};

// Track tour start time for total duration calculation
let tourStartTime = null;

// Track if tour_complete has been sent to prevent duplicates
let tourCompleteTracked = false;

// Guard so inquiry form submit listener is only attached once
let inquiryFormSubmitTrackingSetup = false;

// Utility function to check if gtag is loaded
function isGtagLoaded() {
  return typeof gtag === "function";
}

// Utility function to check if Mixpanel is initialized
function isMixpanelLoaded() {
  return mixpanelEnabled;
}

// Utility function to check if PostHog is initialized
function isPostHogLoaded() {
  return posthogEnabled;
}

/**
 * Track tour complete with sendBeacon for reliable delivery on page unload.
 * This is called by event listeners (beforeunload for desktop, visibilitychange for mobile).
 */
function trackTourCompleteWithBeacon() {
  if (!tourStartTime || tourCompleteTracked) {
    return;
  }
  
  const endTime = new Date().toISOString();
  const totalDuration = Math.round((new Date(endTime) - new Date(tourStartTime)) / 1000);
  const eventData = {
    ...tourProperties,
    start_time: tourStartTime,
    end_time: endTime,
    total_duration: totalDuration,
  };
  
  // Track to Mixpanel if enabled
  if (mixpanelEnabled) {
    try {
      mixpanel.track('tour_complete', eventData, { transport: 'sendBeacon' });
    } catch (error) {
      console.error('Error tracking tour_complete to Mixpanel:', error);
    }
  }
  
  // Track to PostHog if enabled
  if (posthogEnabled) {
    try {
      posthog.capture('tour_complete', eventData);
    } catch (error) {
      console.error('Error tracking tour_complete to PostHog:', error);
    }
  }
  
  tourCompleteTracked = true;
}

/**
 * Handle visibility change for mobile devices.
 * Tracks tour completion when page becomes hidden.
 */
function handleMobileVisibilityChange() {
  if (document.visibilityState === 'hidden' && !tourCompleteTracked) {
    trackTourCompleteWithBeacon();
    document.removeEventListener('visibilitychange', handleMobileVisibilityChange);
  }
}

/**
 * Handle beforeunload for desktop devices.
 * Tracks tour completion when page is unloading.
 */
function handleDesktopBeforeUnload() {
  if (!tourCompleteTracked) {
    trackTourCompleteWithBeacon();
  }
}

/**
 * Set up event listeners for tracking tour completion on page unload.
 * Uses visibilitychange for mobile and beforeunload for desktop.
 */
function setupEndTourTracking() {
  if (isMobile) {
    document.addEventListener('visibilitychange', handleMobileVisibilityChange);
  } else {
    window.addEventListener('beforeunload', handleDesktopBeforeUnload);
  }
}

/**
 * Fire inquiry form submit tracking with current scene context.
 */
function trackInquiryFormSubmit() {
  const eventParams = {};
  if (analyticsSession.currentScene) {
    eventParams.scene_id = analyticsSession.currentScene.scene_id;
    eventParams.scene_title = analyticsSession.currentScene.scene_title;
  }
  trackEvent('tour_inquiry_form_submit', eventParams);
}

/**
 * Set up document-level listeners to track inquiry form submissions.
 * - submit: native form submit inside #form-embed or .form-content.
 * - click: Slate (Technolutions) submit button (class "default form_button_submit") inside same containers;
 *   Slate forms use fetch/XHR and do not fire submit.
 */
function setupInquiryFormSubmitTracking() {
  if (inquiryFormSubmitTrackingSetup) return;
  inquiryFormSubmitTrackingSetup = true;

  document.addEventListener('submit', function handleInquiryFormSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    const inFormEmbed = form.closest('#form-embed') || form.closest('.form-content');
    if (!inFormEmbed) return;

    trackInquiryFormSubmit();
  }, true);

  document.addEventListener('click', function handleSlateSubmitClick(event) {
    const target = event.target && event.target.closest ? event.target.closest('.form_button_submit') : null;
    if (!target) return;

    const inFormEmbed = target.closest('#form-embed') || target.closest('.form-content');
    if (!inFormEmbed) return;

    trackInquiryFormSubmit();
  }, true);
}

/**
 * Initialize Mixpanel SDK with configuration.
 */
function initializeMixpanel() {
  mixpanel.init('08542266b957eba9c9fa68ae9371d1f3', {
    autocapture: true,
    record_sessions_percent: 100,
  });
}

/**
 * Initialize PostHog SDK with configuration.
 */
function initializePostHog() {
  posthog.init('phc_qqrWfMXnD0n8mJRDG22INB7whztuQAUqQb8Suma1LlX', {
    api_host: 'https://us.i.posthog.com',
    defaults: '2025-11-30',
    person_profiles: 'identified_only',
  });
}

/**
 * Set tour metadata properties for inclusion in all tracked events.
 */
function setTourProperties(tourConfig) {
  tourProperties = {
    tour_id: tourConfig.id,
    tour_slug: tourConfig.slug,
  };
}

/**
 * Track initial tour view event.
 */
function trackTourView() {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics] trackTourView called', { mixpanelEnabled, posthogEnabled });
  }
  
  const eventData = {
    ...tourProperties,
    referer: document.referrer || null,
  };
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics] tour_view eventData:', eventData);
  }
  
  if (mixpanelEnabled) {
    try {
      mixpanel.track('tour_view', eventData);
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Tracked tour_view to Mixpanel');
      }
    } catch (error) {
      console.error('[Analytics] Error tracking tour_view to Mixpanel:', error);
    }
  }
  
  if (posthogEnabled) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Tracking tour_view to PostHog:', eventData);
      }
      posthog.capture('tour_view', eventData);
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Successfully called posthog.capture');
      }
    } catch (error) {
      console.error('[Analytics] Error tracking tour_view to PostHog:', error);
    }
  }
}

/**
 * Initialize analytics with tour configuration.
 * Initializes Mixpanel and PostHog if the analyticsExperimental feature flag is present.
 *
 * @param {Object} tourConfig - The tour configuration object
 */
export async function initAnalytics(tourConfig) {
  if (mixpanelEnabled || posthogEnabled) return;
  
  const hasAnalyticsExperimental = tourConfig?.features?.includes("analyticsExperimental");
  if (!hasAnalyticsExperimental) return;
  
  try {
    // Initialize Mixpanel
    try {
      initializeMixpanel();
      mixpanelEnabled = true;
    } catch (error) {
      console.error('Failed to initialize Mixpanel:', error);
    }
    
    // Initialize PostHog
    try {
      initializePostHog();
      posthogEnabled = true;
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] PostHog initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
    
    // Only proceed if at least one service initialized successfully
    if (!mixpanelEnabled && !posthogEnabled) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] No analytics services initialized, returning early');
      }
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Setting tour properties...');
    }
    setTourProperties(tourConfig);
    tourStartTime = new Date().toISOString();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Calling trackTourView...');
    }
    trackTourView();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Setting up end tour tracking...');
    }
    setupEndTourTracking();
    setupInquiryFormSubmitTracking();

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Initialized with analyticsExperimental feature flag', {
        mixpanel: mixpanelEnabled,
        posthog: posthogEnabled,
      });
    }
  } catch (error) {
    console.error('Failed to initialize analytics:', error);
  }
}

// Utility function to track events
function trackEvent(eventName, eventParams) {
  // Merge tour properties into event params
  const eventData = { ...tourProperties, ...eventParams };
  
  // Always track to Google Analytics if available
  if (isGtagLoaded()) {
    // if in dev mode, log the event
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] tracking event', eventName, eventData);
    }
    try {
      gtag("event", eventName, eventData);
    } catch (error) {
      console.error('Error tracking event to Google Analytics:', error);
    }
  } else {
    console.warn(`gtag is not loaded. Event not tracked: ${eventName}`);
  }

  // Conditionally track to Mixpanel if enabled
  if (isMixpanelLoaded()) {
    try {
      mixpanel.track(eventName, eventData);
    } catch (error) {
      console.error('Error tracking event to Mixpanel:', error);
    }
  }
  
  // Conditionally track to PostHog if enabled
  if (isPostHogLoaded()) {
    try {
      posthog.capture(eventName, eventData);
    } catch (error) {
      console.error('Error tracking event to PostHog:', error);
    }
  }
}

// Create an analytics session to track related events
const analyticsSession = {
  currentScene: null,
  
  /**
   * Call this when a scene starts.
   *
   * @param {string} scene_id - The unique identifier for the scene.
   * @param {string} scene_title - The title of the scene.
   * @param {string} scene_source - The source of the scene.
   */
  startScene({ scene_id, scene_title, scene_source }) {
    // End previous scene if exists
    if (this.currentScene) {
      this.endScene(this.currentScene);
    }

    this.currentScene = {
      scene_id,
      scene_title,
      scene_source,
      start_time: new Date().toISOString()
    };
  },

  /**
   * Track when a scene is exited.
   *
   * @param {string} scene_id - The unique identifier for the scene.
   * @param {string} scene_title - The title of the scene.
   */
  endScene({ scene_id, scene_title }) {
    if (!this.currentScene) return;

    const { start_time, scene_source } = this.currentScene;
    const end_time = new Date().toISOString();
    const duration = Math.round((new Date(end_time) - new Date(start_time)) / 1000);

    // Send to Google Analytics
    trackEvent('tour_scene_view', {
      scene_id,
      scene_title,
      start_time,
      end_time,
      duration,
      scene_source
    });

    this.currentScene = null;
  },

  /**
   * Set the source of the current scene.
   *
   * @param {string} source - The source of the scene (e.g., 'grid', 'next', 'previous', 'hotspot').
   */
  setSource(scene_source) {
    if (this.currentScene) {
      this.currentScene.scene_source = scene_source;
    }
  }
};

/**
 * Call this when a scene starts.
 *
 * @param {string} scene_id - The unique identifier for the scene.
 * @param {string} scene_title - The title of the scene.
 * @param {string} scene_source - The source of the scene.
 */
export const trackSceneViewStart = (data) => analyticsSession.startScene(data);

/**
 * Track when a scene is exited.
 *
 * @param {string} scene_id - The unique identifier for the scene.
 * @param {string} scene_title - The title of the scene.
 */
export const trackSceneViewEnd = (data) => analyticsSession.endScene(data);

/**
 * Set the source of the current scene.
 *
 * @param {string} source - The source of the scene (e.g., 'grid', 'next', 'previous', 'hotspot').
 */
export const setTrackSceneSource = (source) => analyticsSession.setSource(source);

/**
 * Track the final scene view. This is useful when the user exits the tour.
 *
 * Uses the last scene that was tracked with trackSceneViewStart.
 * Also tracks total tour duration in Mixpanel if enabled.
 */
export function trackSceneViewFinal() {
  // Track final scene view
  if (analyticsSession.currentScene) {
    analyticsSession.endScene(analyticsSession.currentScene);
  }
  
  // Track total tour duration in Mixpanel (using sendBeacon for reliability)
  trackTourCompleteWithBeacon();
}

/**
 * Track when a hotspot is clicked.
 *
 * @param {string} hotspot_id - The unique identifier for the hotspot.
 * @param {string} hotspot_title - The title of the hotspot.
 * @param {string} hotspot_type - The type of hotspot (e.g., 'link', 'popup').
 * @param {string} scene_id - The unique identifier for the scene where the hotspot was clicked.
 */
export function trackHotspotClick({ hotspot_id, hotspot_type, hotspot_title, scene_id }) {
  trackEvent("tour_hotspot_click", {
    hotspot_id,
    hotspot_title,
    hotspot_type,
    scene_id,
  });
}

/**
 * Track when a Link Button (Request Information or Apply button) is clicked.
 *
 * @param {string} link_text - The text displayed on the button.
 * @param {string} link_url - The URL the button redirects to.
 * @param {string} scene_id - The unique identifier for the scene.
 * @param {string} scene_title - The title of the scene.
 */
export function trackCallToActionClick({ link_text, link_url, scene_id, scene_title }) {
  trackEvent("tour_call_to_action_click", {
    link_text,
    link_url,
    scene_id,
    scene_title,
  });
}

/**
 * Track when a hamburger menu link is clicked.
 *
 * @param {string} link_text - The text of the menu link.
 * @param {string} link_url - The URL the link redirects to, if any.
 */
export function trackHamburgerMenuClick({ link_text, link_url = null }) {
  trackEvent("tour_hamburger_menu_click", {
    link_text,
    link_url,
  });
}

/**
 * Track when the logo is clicked.
 *
 * @param {string} link_url - The URL the logo redirects to.
 */
export function trackLogoClick({ link_url }) {
  trackEvent("tour_logo_click", {
    link_url,
  });
}
